import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import {
  InventorySource,
  CreateInventorySourceRequest,
  UpdateInventorySourceRequest,
} from 'types/inventory';

/**
 * Inventory Source API service
 * Handles CRUD operations for inventory sources
 */
export function useInventorySourceService() {
  const apiClient = useApiClient();

  /**
   * Get all inventory sources for a company
   */
  const getAllInventorySources = useCallback(async (): Promise<
    InventorySource[]
  > => {
    return apiClient.get<InventorySource[]>(API_ROUTES.INVENTORY_SOURCES);
  }, [apiClient]);

  /**
   * Get inventory source by ID
   */
  const getInventorySourceById = useCallback(
    async (id: string): Promise<InventorySource> => {
      return apiClient.get<InventorySource>(
        `${API_ROUTES.INVENTORY_SOURCES}/${id}`,
      );
    },
    [apiClient],
  );

  /**
   * Create a new inventory source
   */
  const createInventorySource = useCallback(
    async (request: CreateInventorySourceRequest): Promise<InventorySource> => {
      return apiClient.post<InventorySource>(
        API_ROUTES.INVENTORY_SOURCES,
        request as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Update an inventory source
   */
  const updateInventorySource = useCallback(
    async (
      id: string,
      request: UpdateInventorySourceRequest,
    ): Promise<InventorySource> => {
      return apiClient.patch<InventorySource>(
        `${API_ROUTES.INVENTORY_SOURCES}/${id}`,
        request as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Delete an inventory source
   */
  const deleteInventorySource = useCallback(
    async (id: string): Promise<void> => {
      await apiClient.delete(`${API_ROUTES.INVENTORY_SOURCES}/${id}`);
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      getAllInventorySources,
      getInventorySourceById,
      createInventorySource,
      updateInventorySource,
      deleteInventorySource,
    }),
    [
      getAllInventorySources,
      getInventorySourceById,
      createInventorySource,
      updateInventorySource,
      deleteInventorySource,
    ],
  );
}
